interface Parameter {
  uid: string;
  name: string;
  value: string;
  position: number;
  namePosition: number;
  valuePosition: number;
}

export interface SerializedStep {
  id: string;
  position: number;
  name: string;
  parameters: Parameter[];
}

interface Protocol {
  steps: SerializedStep[];
}

import type { ValidationError } from "@/state/validationState";
import { Node } from "@tiptap/pm/model";
import { v4 as v4 } from "uuid";
import type { Schema } from "@tiptap/pm/model";
import type {
  ConfigStep,
  LiquidClass,
} from "@/automation/system-config/systemConfigParser";
import {
  getDefaultLiquidClass,
  getLiquidClassSettings,
} from "@/automation/utils/liquidClassUtils";

export function serializeProtocolSteps(doc: Node): Protocol {
  const serializedSteps: SerializedStep[] = [];

  // Process each node in the document, tracking positions
  doc.descendants((node: Node, pos: number) => {
    if (node.type.name !== "step") return true;

    const step: SerializedStep = {
      id: node.attrs.uid,
      position: pos + 1,
      name: "",
      parameters: [],
    };

    // Extract step name
    node.descendants((childNode: Node) => {
      if (childNode.type.name === "stepName" && childNode.textContent) {
        step.name = childNode.textContent;
        return false; // Stop traversing this branch
      }
      return true;
    });

    // Extract parameters
    node.descendants((childNode: Node, childPos: number) => {
      if (childNode.type.name === "parameter") {
        let paramName = "";
        let paramValue = "";
        let namePosition = 0;
        let valuePosition = 0;
        const paramPosition = pos + childPos + 1; // Add +1 offset consistently
        const paramUid = childNode.attrs.uid || crypto.randomUUID(); // Get UID from attrs or generate new one

        childNode.descendants((paramChild: Node, paramChildPos: number) => {
          if (paramChild.type.name === "paramName") {
            paramName = paramChild.textContent;
            namePosition = paramPosition + paramChildPos + 1;
          }
          if (paramChild.type.name === "paramValue") {
            // Check if paramValue contains a selectValue node
            let hasSelectValue = false;

            paramChild.descendants((valueChild: Node) => {
              if (
                valueChild.type.name === "selectValue" &&
                valueChild.attrs.selectedId
              ) {
                // Use the selectedId as the parameter value
                paramValue = valueChild.attrs.selectedId;
                hasSelectValue = true;
                return false; // Stop traversing this branch
              }
              return true;
            });

            // If no selectValue was found, use the textContent as before
            if (!hasSelectValue) {
              paramValue = paramChild.textContent;
            }

            valuePosition = paramPosition + paramChildPos + 1;
          }
          return true;
        });

        step.parameters.push({
          uid: paramUid,
          name: paramName,
          value: paramValue,
          position: paramPosition,
          namePosition,
          valuePosition,
        });
      }
      return true;
    });

    serializedSteps.push(step);
    return true; // Continue traversing
  });

  return {
    steps: serializedSteps,
  };
}

export function findStepById(
  doc: Node,
  stepId: string
): { position: number; node: Node } | null {
  let foundStep: { position: number; node: Node } | null = null;

  doc.descendants((node: Node, pos: number) => {
    if (node.type.name === "step" && node.attrs.uid === stepId) {
      foundStep = {
        position: pos,
        node: node,
      };
      return false; // Stop traversing once found
    }
    return true;
  });

  return foundStep;
}

/**
 * Groups validation errors by their stepId
 *
 * @param allErrors List of all validation errors
 * @returns An object with stepIds as keys and arrays of errors as values
 */
export function groupErrorsByStep(
  allErrors: ValidationError[]
): Record<string, ValidationError[]> {
  const errorsByStep: Record<string, ValidationError[]> = {};

  for (const error of allErrors) {
    if (!error.stepId) continue;

    if (!errorsByStep[error.stepId]) {
      errorsByStep[error.stepId] = [];
    }

    errorsByStep[error.stepId].push(error);
  }

  return errorsByStep;
}

/**
 * Creates a step node with required parameters in a single transaction
 *
 * @param schema ProseMirror schema
 * @param itemName Name of the step
 * @param requiredParams Array of required parameter objects with 'name' property
 * @param objects Optional objects parameter
 * @param configStep Optional config step for liquid class defaults
 * @param liquidClasses Optional available liquid classes
 * @returns The created step node with UIDs assigned
 */
export function createStepNodeWithRequiredParams(
  schema: Schema,
  itemName: string,
  requiredParams: Array<{ id?: string; name: string; required: boolean }>,
  objects?: string,
  configStep?: ConfigStep,
  liquidClasses?: LiquidClass[]
) {
  // Initialize step settings with liquid class defaults if required
  let initialStepSettings = {};
  let defaultLiquidClass: LiquidClass | null = null;

  if (configStep?.requireLiquidClass && liquidClasses) {
    try {
      defaultLiquidClass = getDefaultLiquidClass(configStep, liquidClasses);
      const settings = getLiquidClassSettings(defaultLiquidClass);
      initialStepSettings = {
        liquidClass: defaultLiquidClass.id,
        ...settings,
      };
    } catch (error) {
      console.error("Failed to apply liquid class defaults:", error);
      // Proceed without defaults if an error occurs
    }
  }

  // Node Construction
  const parameterNodesArray = requiredParams.map((param) => {
    const paramNameNode = schema.nodes.paramName.create(
      null,
      schema.text(param.name)
    );

    // Check if this is a "Liquid class" parameter and we have a default liquid class
    let paramValueNode: Node;
    if (param.name === "Liquid class" && defaultLiquidClass) {
      // Create paramValue with the liquid class id as content
      paramValueNode = schema.nodes.paramValue.create(
        null,
        schema.text(defaultLiquidClass.id)
      );
    } else {
      // Create empty paramValue for other parameters
      paramValueNode = schema.nodes.paramValue.create();
    }

    // Assign UID to the parameter node
    return schema.nodes.parameter.create(
      { uid: v4(), configParamId: param.id ?? null },
      [paramNameNode, paramValueNode]
    );
  });

  const parametersNode =
    parameterNodesArray.length > 0
      ? schema.nodes.parameters.create(null, parameterNodesArray)
      : schema.nodes.parameters.create(); // Create empty parameters node if no required params

  const stepNameNode = schema.nodes.stepName.create(
    null,
    schema.text(itemName)
  );

  // Create the step node with all necessary components and assign a UID
  return schema.nodes.step.create(
    {
      uid: v4(),
      objects,
      stepSettings: initialStepSettings,
      configStepId: configStep?.id ?? null,
    },
    [
      stepNameNode,
      parametersNode,
      schema.nodes.howtos.create({ uid: v4() }),
      schema.nodes.notes.create({ uid: v4() }),
    ]
  );
}
